import React, {useState, useEffect} from "react";
import {useTranslation} from "react-i18next";
import {api} from "../../../config/api";
import secureStorage from "../../../utils/secureStorage";
import {toLocalDateStr} from "../../../utils/dateHelpers";

const fmt = (v) =>
    Number(v || 0).toLocaleString("fr-FR", {minimumFractionDigits: 2, maximumFractionDigits: 2}) + " DH";

// Ordre d'affichage des modes de commande.
const ORDER_TYPE_KEYS = ["emporter", "sur_place"];

const OrderTypeMetrics = ({_dateRange, _startTime, _endTime, isDarkMode = false, refreshKey = 0}) => {
    const {t} = useTranslation("activite");
    const [data, setData]       = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError]     = useState(null);

    useEffect(() => {
        if (!_dateRange?.[0]?.startDate || !_dateRange?.[0]?.endDate) return;

        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const account = await secureStorage.getItem("selectedAccount");
                if (!account?.id) return;

                const start = toLocalDateStr(_dateRange[0].startDate);
                const end   = toLocalDateStr(_dateRange[0].endDate);
                const startTime = _startTime || "00:00";
                const endTime   = _endTime || "23:59";

                const res = await api.get(
                    `/activite/order_type_metrics?startDate=${start}&endDate=${end}&startTime=${startTime}&endTime=${endTime}`,
                    {headers: {"X-account-id": account.id}}
                );
                setData(res.data || null);
            } catch (err) {
                console.error("OrderTypeMetrics fetch error:", err);
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [_dateRange, _startTime, _endTime, refreshKey]);

    const base = isDarkMode ? "text-gray-100 bg-gray-900" : "text-gray-900 bg-white";

    if (loading) {
        return (
            <div className={`w-full py-10 flex items-center justify-center ${base}`}>
                <div className="animate-spin w-5 h-5 border-2 border-primary border-t-transparent rounded-full"/>
                <span className="ml-3 text-sm text-gray-400">{t("orderTypeMetrics.loading")}</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`w-full py-8 flex items-center justify-center ${base}`}>
                <p className="text-sm text-red-400">{t("orderTypeMetrics.loadError")}</p>
            </div>
        );
    }

    if (!data) return null;

    // Même format que les autres tableaux : NOM / QTÉ / TOTAL.
    const rows = ORDER_TYPE_KEYS
        .map((key) => data[key] || {})
        .filter((d) => d.label || d.orders_count !== undefined)
        .map((d) => ({
            name:     d.label || "",
            quantity: d.orders_count || 0,
            total:    d.total_revenue || 0,
        }));

    return (
        <div className={`w-full ${base}`}>
            <div className="overflow-x-auto">
                <table className="w-full border-collapse mt-4">
                    <thead>
                    <tr>
                        <th className={`text-left py-2 px-4 text-xs font-medium border-b ${isDarkMode ? "text-gray-400 border-gray-700" : "text-gray-500 border-gray-200"}`}>{t("expandableTable.columns.name")}</th>
                        <th className={`text-left py-2 px-4 text-xs font-medium border-b ${isDarkMode ? "text-gray-400 border-gray-700" : "text-gray-500 border-gray-200"}`}>{t("expandableTable.columns.quantity")}</th>
                        <th className={`text-right py-2 px-4 text-xs font-medium border-b ${isDarkMode ? "text-gray-400 border-gray-700" : "text-gray-500 border-gray-200"}`}>{t("expandableTable.columns.total")}</th>
                    </tr>
                    </thead>
                    <tbody>
                    {rows.map((item, index) => (
                        <tr key={index}
                            className={`transition-colors ${isDarkMode ? "hover:bg-gray-800 border-gray-700" : "hover:bg-gray-50"}`}>
                            <td className={`py-3 px-4 text-sm capitalize ${isDarkMode ? "text-gray-300 border-gray-700" : "text-gray-700"}`}>{item.name}</td>
                            <td className={`py-3 px-4 text-sm ${isDarkMode ? "text-gray-300 border-gray-700" : "text-gray-700"}`}>{item.quantity}</td>
                            <td className={`py-3 px-4 text-sm text-right font-medium ${isDarkMode ? "text-gray-200 border-gray-700" : "text-gray-700"}`}>
                                {fmt(item.total)}
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
            {rows.length === 0 && (
                <div className="flex justify-center items-center py-8">
                    <p className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>{t("expandableTable.noData")}</p>
                </div>
            )}
        </div>
    );
};

export default OrderTypeMetrics;
